import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { ArrowLeft, PhoneCall, ArrowRight, CalendarClock, MessageSquareQuote } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RELANCE_LABELS = {
    relance_1: { label: 'Relance 1', cls: 'bg-orange-100 text-orange-700' },
    relance_2: { label: 'Relance 2', cls: 'bg-orange-100 text-orange-700' },
    relance_3: { label: 'Relance 3', cls: 'bg-orange-100 text-orange-700' },
    relance_whatsapp: { label: 'WhatsApp', cls: 'bg-green-100 text-green-700' },
    decision_pas_prise: { label: 'Decision pas prise', cls: 'bg-gray-100 text-gray-600' },
    interesse: { label: 'Interesse', cls: 'bg-blue-100 text-blue-700' },
};

export default function ClientsRelancer() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetch = async () => {
            try {
                const res = await axios.get(`${API_URL}/api/dashboard/clients-relancer`, { withCredentials: true });
                setItems(res.data);
            } catch {
                // ignore
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, []);

    return (
        <Layout>
            <div className="space-y-6" data-testid="clients-relancer-page">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/')} className="flex items-center gap-1 text-gray-500 hover:text-[#1B365D] transition-colors text-sm" data-testid="relancer-back-btn">
                        <ArrowLeft className="w-4 h-4" /> Retour
                    </button>
                    <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Clients a relancer
                    </h1>
                </div>

                <Card className="shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-semibold text-[#1B365D] flex items-center gap-2">
                            <PhoneCall className="w-5 h-5 text-[#C9A962]" />
                            Clients a relancer
                            <Badge className="bg-gray-100 text-gray-600 border border-gray-200 text-xs ml-1">{items.length}</Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1B365D]" />
                            </div>
                        ) : items.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                                    <PhoneCall className="h-8 w-8 text-gray-400" />
                                </div>
                                <p className="text-gray-500 font-medium">Aucun client a relancer</p>
                                <p className="text-gray-400 text-sm mt-1">Les clients a relancer apparaitront ici automatiquement</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {items.map((c) => {
                                    const rl = RELANCE_LABELS[c.statut] || { label: c.statut, cls: 'bg-gray-100 text-gray-600' };
                                    const lastDate = c.last_action ? new Date(c.last_action).toLocaleDateString('fr-FR') : '-';
                                    // Retrieve last remark from history (non-null remark)
                                    const history = Array.isArray(c.relance_history) ? c.relance_history : [];
                                    const lastWithRemark = [...history].reverse().find(h => h.remark && h.remark.trim());
                                    const remark = lastWithRemark?.remark || '';
                                    const nextDateStr = c.next_relance_date
                                        ? new Date(c.next_relance_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
                                        : '';
                                    const isOverdue = c.next_relance_date && new Date(c.next_relance_date) < new Date();

                                    return (
                                        <div key={c.devis_id}
                                            onClick={() => navigate(`/devis/${c.devis_id}`)}
                                            className="p-3 rounded-xl border border-gray-100 hover:border-[#C9A962]/40 hover:bg-amber-50/30 cursor-pointer transition-all group"
                                            data-testid={`relancer-${c.devis_id}`}>
                                            <div className="flex items-start gap-3">
                                                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center shrink-0 mt-0.5">
                                                    <PhoneCall className="w-3.5 h-3.5 text-orange-600" />
                                                </div>
                                                <div className="flex-1 min-w-0 space-y-1.5">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-semibold text-gray-800 truncate group-hover:text-[#1B365D]">{c.nom}</p>
                                                            <p className="text-xs text-gray-400">Dernière action : {lastDate}</p>
                                                        </div>
                                                        <div className="flex items-center gap-2 shrink-0">
                                                            {c.within_21_days && (
                                                                <Badge className="text-[10px] bg-red-100 text-red-700 border border-red-200" data-testid={`badge-21j-${c.devis_id}`}>
                                                                    -21 jours
                                                                </Badge>
                                                            )}
                                                            <Badge className={`text-[10px] ${rl.cls}`}>{rl.label}</Badge>
                                                            <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-[#C9A962] transition-colors" />
                                                        </div>
                                                    </div>
                                                    {remark && (
                                                        <div className="flex items-start gap-1.5 text-xs text-gray-600 bg-amber-50/70 border border-amber-100 rounded-md px-2.5 py-1.5">
                                                            <MessageSquareQuote className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                                                            <span className="whitespace-pre-wrap break-words">{remark}</span>
                                                        </div>
                                                    )}
                                                    {nextDateStr && (
                                                        <div className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2 py-0.5 border ${isOverdue ? 'bg-red-50 text-red-700 border-red-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                                                            <CalendarClock className="w-3 h-3" />
                                                            {isOverdue ? 'Relance due' : 'Relance planifiée'} le {nextDateStr}
                                                        </div>
                                                    )}
                                                    {c.date_chargement && (
                                                        <p className="text-[10px] text-gray-400">Chargement : {c.date_chargement}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
